import React, { FC, useEffect, useMemo, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { addApplicationFavorite, delApplicationFavorite, fetchApplicationList } from '../../../../states/containers/analyze/analyzeSlice'
import {
  updateCurrentAnalyzeSelectDate,
  updateCurrentAnalyzeSelectTeam,
  updateCurrentAnalyzeSelectMember,
} from '../../../../states/containers/common/currentSlice'
import { fetchMemberList } from '../../../../states/containers/member/listSlice'
import { RootState } from '../../../../states/containers/store'
import { CompanyServices } from '../../../../states/types/analyze'
import { LangValue } from '../../../../states/types/common'
import { endOfMonth, format, nowDate, prevMonth, startOfMonth, yesterDay } from '../../../../utils/date'
import { getGetPrams, setPath, setUrlParam } from '../../../../utils/url'
import ApplicationCardList from '../../../components/analyze/ApplicationCardList'
import DateModule from '../../../components/analyze/DateModule'
import SearchConditionModule from '../../../components/analyze/SearchConditionModule'
import SearchModule from '../../../components/analyze/SearchModule'
import Loading from '../../../components/common/Loading'
import { URL } from '../../../constants/url'

interface GetParams {
  date: string
  team_ids: string
  member_ids: string
}

interface RequestParams {
  start_date: string,
  end_date: string,
  team_ids?: number[]
  member_ids?: number[]
}

const Index: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    analyze,
    commonPart,
    login,
    current,
  } = useSelector((state: RootState) => state)

  const [applicationList, setApplicationList] = useState<CompanyServices[]>()
  const [date, setDate] = useState<string>("")
  const [teamIds, setTeamIds] = useState<number[]>([])
  const [memberIds, setMemberIds] = useState<number[]>([])

  const endDate = useMemo(() => {
    if (Number(format(date, "YYYYMM")) === Number(nowDate("YYYYMM"))) {
      return yesterDay("YYYY-MM-DD")
    }
    return endOfMonth(date, "YYYY-MM-DD")
  }, [date])

  const clickApp = (name: string) => {
    history.push(`${setPath(setUrlParam(
      URL.APP_ANALYZE_DETAIL.path,
      [
        { key: 'name', value: name },
      ],
    ), language)}`)
  }

  const clickFab = (name: string) => {
    if (!applicationList) return
    const target = applicationList.find((value) => value.name === name)
    if (!target) return
    setApplicationList(
      [...applicationList.map((value) => (value.name === name ? { ...value, favorite: !value.favorite } : value))],
    )
    const tmpName = target.company_service_name
      ? `${target.company_service_name}(${name})`
      : name
    if (target.favorite) {
      dispatch(
        delApplicationFavorite(
          { service_name: name },
          () => {
            alert.success(`${tmpName}のピンを外しました。`)
          },
        ),
      )
    } else {
      dispatch(
        addApplicationFavorite(
          { service_name: name },
          () => {
            alert.success(`${tmpName}にピンを刺しました。`)
          },
        ),
      )
    }
  }

  useEffect(() => {
    const getParams = getGetPrams(location.search) as GetParams
    if (getParams.date) {
      setDate(format(getParams.date))
    } else if (current.analyzeSelectDate) {
      setDate(format(current.analyzeSelectDate))
    } else if ((Number(startOfMonth(nowDate(), "YYYYMMDD")) + 7) < Number(nowDate("YYYYMMDD"))) {
      setDate(startOfMonth(nowDate(), "YYYY-MM-DD"))
    } else {
      setDate(startOfMonth(prevMonth(nowDate()), "YYYY-MM-DD"))
    }
    if (getParams.team_ids) {
      setTeamIds(getParams.team_ids.split(',').map((value) => Number(value)))
    } else if (current.analyzeSelectTeam) {
      setTeamIds(current.analyzeSelectTeam.split(',').map((value) => Number(value)))
    }
    if (getParams.member_ids) {
      setMemberIds(getParams.member_ids.split(',').map((value) => Number(value)))
    } else if (current.analyzeSelectMember) {
      setMemberIds(current.analyzeSelectMember.split(',').map((value) => Number(value)))
    }
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    if (!date) return
    const params: RequestParams = {
      start_date: startOfMonth(date, "YYYY-MM-DD"),
      end_date: endDate,
    }
    const query = [`date=${startOfMonth(date, "YYYY-MM-DD")}`]
    if (teamIds.length) {
      params.team_ids = teamIds
      query.push(`team_ids=${teamIds}`)
    }
    if (memberIds.length) {
      params.member_ids = memberIds
      query.push(`member_ids=${memberIds}`)
    }
    dispatch(updateCurrentAnalyzeSelectDate(date))
    dispatch(updateCurrentAnalyzeSelectTeam(teamIds.join(',')))
    dispatch(updateCurrentAnalyzeSelectMember(memberIds.join(',')))
    dispatch(fetchApplicationList(params))
    history.replace(`${setPath(URL.APP_ANALYZE.path, language)}?${query.join('&')}`)
  }, [date, teamIds, memberIds])

  useEffect(() => {
    setApplicationList(analyze.applicationList?.company_services)
  }, [analyze.applicationList])

  useEffect(() => {
    if (!login.accessToken) return
    if (!commonPart.company?.id) return
    dispatch(
      fetchMemberList(),
    )
  }, [login.accessToken, commonPart.company])

  return (
    <section className="analyze_page">
      <Helmet>
        <title>Qasee - 組織分析 - アプリケーション分析</title>
      </Helmet>
      <section className="breadcrumb">
        <Link
          to={setPath(URL.ANALYZE.path, language)}
        >
          組織分析
        </Link>
        &nbsp;&gt;&nbsp;
        アプリケーション分析
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          アプリケーション分析
        </h1>
        <div className="tools">
          <SearchModule
            callbackTeamIds={setTeamIds}
            callbackMemberIds={setMemberIds}
          />
          <DateModule
            date={date}
            changeCallback={(callback_date) => {
              setDate(callback_date)
            }}
          />
        </div>
      </section>
      <SearchConditionModule
        name="アプリケーション分析一覧"
        date={date}
        teamIds={teamIds}
        memberIds={memberIds}
      />
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              アプリケーション分析一覧
            </div>
          </div>
          <div className="block-detail">
            {
              analyze.isLoading ? (
                <>
                  <Loading />
                </>
              ) : (
                <>
                  {
                    applicationList?.length ? (
                      <ApplicationCardList
                        clickAppCallback={clickApp}
                        clickFabCallback={clickFab}
                        list={applicationList}
                      />
                    ) : (
                      <div className="no_data_area">
                        <div className="no_data_title">
                          データがありません
                        </div>
                        <div className="no_data_text">
                          対象アプリケーションの期間条件や絞り込み条件をご確認ください。
                        </div>
                      </div>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </section>
    </section>
  )
}

export default Index
